import { useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

type UseDirtyFormikOptions = {
    ignoreSearchParams?: boolean;
};

export const DirtyFormikListener = (options?: UseDirtyFormikOptions) => {
    useDirtyFormik(options);
    return null;
};

export const useDirtyFormik = ({ ignoreSearchParams = false }: UseDirtyFormikOptions = {}) => {
    const { dirty: isDirty } = useFormikContext();
    const intl = useIntl();
    const originalPushStateRef = useRef(window.history.pushState);
    const originalReplaceStateRef = useRef(window.history.replaceState);

    useEffect(() => {
        const message = intl.formatMessage({
            id: 'common.unsavedChangesConfirmation',
            defaultMessage: 'You have unsaved changes. Are you sure you want to leave this page?',
        });

        // Handle page reload/close
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (isDirty) {
                if (!confirm(message)) {
                    event.preventDefault();
                }
            }
        };

        const shouldConfirmNavigation = (url: Parameters<typeof history.pushState>[2]) => {
            if (!isDirty) {
                return false;
            }

            if (!ignoreSearchParams || url == null) {
                return true;
            }

            const nextUrl = new URL(String(url), window.location.href);
            return nextUrl.origin !== window.location.origin || nextUrl.pathname !== window.location.pathname;
        };

        // Intercept history.pushState and history.replaceState
        const handlePushState = function (this: History, ...args: Parameters<typeof history.pushState>) {
            if (shouldConfirmNavigation(args[2]) && !confirm(message)) {
                return;
            }
            return originalPushStateRef.current.apply(this, args);
        };

        const handleReplaceState = function (this: History, ...args: Parameters<typeof history.replaceState>) {
            if (shouldConfirmNavigation(args[2]) && !confirm(message)) {
                return;
            }
            return originalReplaceStateRef.current.apply(this, args);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.history.pushState = handlePushState;
        window.history.replaceState = handleReplaceState;

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.history.pushState = originalPushStateRef.current;
            window.history.replaceState = originalReplaceStateRef.current;
        };
    }, [ignoreSearchParams, isDirty, intl]);
};
